import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, ExternalLink, KeyRound, Plus, Receipt, RefreshCw, Search, User, UserCheck, Users as UsersIcon, Wallet } from 'lucide-react';
import { User as UserType } from '../../types';
import apiService from '../../services/api';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEscapeToClose from '../../hooks/useEscapeToClose';

const STATUS_BADGE: Record<string, string> = {
  activo: 'bg-green-100 text-green-700 border-green-200',
  inactivo: 'bg-yellow-100 text-yellow-700 border-yellow-200'
};

const CLIENT_TYPE_LABEL: Record<string, string> = {
  empresa: 'Empresa',
  negocio: 'Negocio',
  particular: 'Particular'
};

const CLIENT_TYPE_BADGE: Record<string, string> = {
  empresa: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  negocio: 'bg-blue-100 text-blue-700 border-blue-200',
  particular: 'bg-gray-100 text-gray-700 border-gray-200'
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const StatusBadge: React.FC<{ status: 'activo' | 'inactivo' }> = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${STATUS_BADGE[status]}`}>
    {status === 'activo' ? 'Activo' : 'Inactivo'}
  </span>
);

const ClientTypeBadge: React.FC<{ clientType?: string }> = ({ clientType }) => {
  const type = clientType || 'particular';
  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${CLIENT_TYPE_BADGE[type] || CLIENT_TYPE_BADGE.particular}`}>
      {CLIENT_TYPE_LABEL[type] || CLIENT_TYPE_LABEL.particular}
    </span>
  );
};

const DeleteConfirm: React.FC<{ onConfirm: () => void; onCancel: () => void }> = ({ onConfirm, onCancel }) => (
  <div className="inline-flex items-center gap-2 text-xs">
    <span className="text-gray-600">¿Confirmar?</span>
    <button onClick={onConfirm} className="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700">Sí</button>
    <button onClick={onCancel} className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50">No</button>
  </div>
);

const SkeletonRows: React.FC = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-pulse">
      {[1, 2, 3].map((i) => <div key={i} className="h-24 bg-white rounded-xl border border-gray-100 shadow-sm" />)}
    </div>
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 animate-pulse space-y-3">
      {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-12 bg-gray-100 rounded-lg" />)}
    </div>
  </div>
);

const EmptyState: React.FC<{ withFilters: boolean }> = ({ withFilters }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
    <UsersIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
    <p className="text-gray-800 font-medium">{withFilters ? 'No hay resultados para los filtros aplicados' : 'No hay clientes cargados'}</p>
    <p className="text-sm text-gray-500 mt-1">{withFilters ? 'Probá con otros filtros de búsqueda.' : 'Los clientes que cargues aparecerán aquí y podrás seleccionarlos en Cotizador y Remitos.'}</p>
  </div>
);

const emptyClientForm = {
  name: '',
  email: '',
  phone: '',
  company: '',
  cuit: '',
  address: '',
  password: '',
  isActive: true,
  clientType: 'particular' as 'empresa' | 'negocio' | 'particular'
};

// Resumen y filtros ocultos mientras haya pocos clientes; poner en true para volver a mostrarlos.
const SHOW_SUMMARY_AND_FILTERS = false;

const Clientes: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<UserType[]>([]);
  const [receiptCounts, setReceiptCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedClient, setSelectedClient] = useState<UserType | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(emptyClientForm);
  const [isSavingCreate, setIsSavingCreate] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingClient, setEditingClient] = useState<UserType | null>(null);
  const [editForm, setEditForm] = useState(emptyClientForm);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [passwordUser, setPasswordUser] = useState<UserType | null>(null);
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  useBodyScrollLock(!!(selectedClient || showCreateModal || editingClient || passwordUser));
  useEscapeToClose(!!selectedClient, () => setSelectedClient(null));
  useEscapeToClose(showCreateModal, () => closeCreateModal());
  useEscapeToClose(!!editingClient, () => closeEditModal());
  useEscapeToClose(!!passwordUser, () => closePasswordModal());

  const fetchClients = async (manualRetry = false) => {
    if (manualRetry) setIsRetrying(true);
    try {
      setLoadError(null);
      const response = await apiService.getUsers({ role: 'cliente', page: 1, limit: 500 });
      setClients(response.users || []);
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setLoadError(detail || 'No se pudieron cargar los clientes.');
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  };

  const fetchReceiptCounts = async () => {
    try {
      const response = await apiService.getReceiptCountsByClient();
      setReceiptCounts(response.counts || {});
    } catch {
      setReceiptCounts({});
    }
  };

  useEffect(() => {
    fetchClients();
    fetchReceiptCounts();
  }, []);

  const goToClientReceipts = (clientId: number) => {
    navigate(`/admin/remitos?clientId=${clientId}`);
  };

  const goToClientPage = (client: UserType) => {
    navigate(`/admin/clientes/${client.id}`, { state: { client } });
  };

  const filteredClients = useMemo(() => {
    return clients.filter((c) => {
      if (selectedStatus === 'active' && !c.isActive) return false;
      if (selectedStatus === 'inactive' && c.isActive) return false;
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        (c.company || '').toLowerCase().includes(q) ||
        (c.cuit || c.taxId || '').toLowerCase().includes(q)
      );
    });
  }, [clients, selectedStatus, searchTerm]);

  const inactiveCount = useMemo(() => clients.filter((c) => !c.isActive).length, [clients]);

  const toggleStatus = async (id: number) => {
    try {
      setInlineError(null);
      const res = await apiService.toggleUserStatus(id);
      setClients((prev) => prev.map((c) => (c.id === id ? { ...c, isActive: res.user.isActive } : c)));
      setSelectedClient((prev) => (prev && prev.id === id ? { ...prev, isActive: res.user.isActive } : prev));
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setInlineError(detail || 'No se pudo actualizar el estado.');
    }
  };

  const deleteClient = async (client: UserType) => {
    try {
      setInlineError(null);
      await apiService.deleteUserByAdmin(client.id);
      setClients((prev) => prev.filter((c) => c.id !== client.id));
      if (selectedClient?.id === client.id) setSelectedClient(null);
      setConfirmDeleteId(null);
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setInlineError(detail || 'No se pudo eliminar el cliente.');
    }
  };

  const openCreateModal = () => {
    setCreateForm(emptyClientForm);
    setCreateError(null);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setIsSavingCreate(false);
  };

  const saveCreateClient = async () => {
    const name = createForm.name.trim();
    const email = createForm.email.trim();
    const cuit = createForm.cuit.replace(/\D/g, '');
    if (name.length < 2 || !email.includes('@') || cuit.length !== 11) {
      setCreateError('Completa nombre y email válidos.');
      return;
    }
    try {
      setIsSavingCreate(true);
      setCreateError(null);
      const response = await apiService.createUserByAdmin({
        name,
        email,
        phone: createForm.phone.trim() || undefined,
        company: createForm.company.trim() || undefined,
        cuit,
        address: createForm.address.trim() || undefined,
        role: 'cliente',
        isActive: createForm.isActive,
        clientType: createForm.clientType,
        password: createForm.password.trim() || undefined
      });
      setClients((prev) => [response.user, ...prev]);
      closeCreateModal();
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setCreateError(detail || 'No se pudo crear el cliente.');
    } finally {
      setIsSavingCreate(false);
    }
  };

  const handleEdit = (client: UserType) => {
    setEditingClient(client);
    setEditForm({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      company: client.company || '',
      cuit: client.cuit || client.taxId || '',
      address: client.address || '',
      password: '',
      isActive: client.isActive,
      clientType: (client.clientType || 'particular') as 'empresa' | 'negocio' | 'particular'
    });
  };

  const closeEditModal = () => {
    setEditingClient(null);
    setIsSavingEdit(false);
  };

  const saveEditClient = async () => {
    if (!editingClient) return;
    const cuit = editForm.cuit.replace(/\D/g, '');
    if (cuit.length !== 11) {
      setInlineError('Ingresá un CUIT válido de 11 dígitos.');
      return;
    }
    try {
      setIsSavingEdit(true);
      setInlineError(null);
      const response = await apiService.updateUserByAdmin(editingClient.id, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim() || undefined,
        company: editForm.company.trim() || undefined,
        cuit,
        address: editForm.address.trim() || undefined,
        role: 'cliente',
        isActive: editForm.isActive,
        clientType: editForm.clientType
      });
      setClients((prev) => prev.map((c) => (c.id === editingClient.id ? { ...c, ...response.user } : c)));
      closeEditModal();
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setInlineError(detail || 'No se pudo guardar el cliente.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const openPasswordModal = (client: UserType) => {
    setPasswordUser(client);
    setPasswordForm({ newPassword: '', confirmPassword: '' });
    setPasswordError(null);
  };

  const closePasswordModal = () => {
    setPasswordUser(null);
    setPasswordForm({ newPassword: '', confirmPassword: '' });
    setPasswordError(null);
    setIsSavingPassword(false);
  };

  const savePassword = async () => {
    if (!passwordUser) return;
    if (passwordForm.newPassword.trim().length < 6) {
      setPasswordError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Las contraseñas no coinciden.');
      return;
    }
    try {
      setIsSavingPassword(true);
      setPasswordError(null);
      await apiService.updateUserPasswordByAdmin(passwordUser.id, passwordForm.newPassword);
      closePasswordModal();
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setPasswordError(detail || 'No se pudo actualizar la contraseña.');
    } finally {
      setIsSavingPassword(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
          <p className="text-gray-600">Cargando información...</p>
        </div>
        <SkeletonRows />
      </div>
    );
  }

  const hasFilters = selectedStatus !== 'all' || searchTerm.trim().length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
          <p className="text-gray-600">{clients.length} clientes cargados · {inactiveCount} inactivos</p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          Nuevo cliente
        </button>
      </div>

      {loadError && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 flex items-center justify-between gap-4">
          <span>{loadError}</span>
          <button
            onClick={() => fetchClients(true)}
            disabled={isRetrying}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded border border-red-300 hover:bg-red-100 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
            Reintentar
          </button>
        </div>
      )}

      {inlineError && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{inlineError}</div>}

      {SHOW_SUMMARY_AND_FILTERS && (<>
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className="bg-white p-3 sm:p-5 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center"><User className="hidden sm:block h-6 w-6 text-blue-600" /><div className="sm:ml-3"><p className="text-xs sm:text-sm text-gray-600">Total</p><p className="text-xl sm:text-2xl font-bold">{clients.length}</p></div></div>
        </div>
        <div className="bg-white p-3 sm:p-5 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center"><UserCheck className="hidden sm:block h-6 w-6 text-green-600" /><div className="sm:ml-3"><p className="text-xs sm:text-sm text-gray-600">Activos</p><p className="text-xl sm:text-2xl font-bold">{clients.filter((c) => c.isActive).length}</p></div></div>
        </div>
        <div className="bg-white p-3 sm:p-5 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center"><UsersIcon className="hidden sm:block h-6 w-6 text-gray-700" /><div className="sm:ml-3"><p className="text-xs sm:text-sm text-gray-600">Inactivos</p><p className="text-xl sm:text-2xl font-bold">{inactiveCount}</p></div></div>
        </div>
      </div>

      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre, email, empresa o CUIT..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <option value="all">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="inactive">Inactivo</option>
        </select>
      </div>
      </>)}

      {filteredClients.length === 0 ? (
        <EmptyState withFilters={hasFilters} />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="divide-y md:hidden">
          {filteredClients.map((c) => (
            <div key={c.id} className={`p-4 space-y-2 ${!c.isActive ? 'border-l-2 border-l-red-400' : ''}`}>
              <button onClick={() => setSelectedClient(c)} className="block w-full text-left space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span onClick={(e) => { e.stopPropagation(); goToClientPage(c); }} className="font-medium text-primary-700 hover:underline">{c.name}</span>
                  <StatusBadge status={c.isActive ? 'activo' : 'inactivo'} />
                </div>
                <div className="flex items-center gap-2"><ClientTypeBadge clientType={c.clientType} />{c.company && <span className="text-sm text-gray-500">{c.company}</span>}</div>
                <p className="text-sm text-gray-500">{c.email}{c.phone ? ` · ${c.phone}` : ''}</p>
              </button>
              <div className="flex items-center justify-between gap-2 pt-1">
                <button
                  onClick={() => goToClientReceipts(c.id)}
                  title="Ver remitos de este cliente"
                  className="inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <Receipt className="h-3.5 w-3.5" />
                  {receiptCounts[String(c.id)] || 0} remito{(receiptCounts[String(c.id)] || 0) === 1 ? '' : 's'}
                </button>
                <div className="flex items-center gap-1">
                  <button onClick={() => goToClientPage(c)} className="p-1.5 text-primary-600 hover:bg-primary-50 rounded" title="Página del cliente"><ExternalLink className="h-4 w-4" /></button>
                  <button onClick={() => navigate(`/admin/cuentas-corrientes/client/${c.id}`)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" title="Ver cuenta corriente"><Wallet className="h-4 w-4" /></button>
                  <button onClick={() => handleEdit(c)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="Editar"><Edit className="h-4 w-4" /></button>
                  <button onClick={() => openPasswordModal(c)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded" title="Cambiar contraseña"><KeyRound className="h-4 w-4" /></button>
                </div>
              </div>
              {confirmDeleteId === c.id ? (
                <DeleteConfirm onConfirm={() => deleteClient(c)} onCancel={() => setConfirmDeleteId(null)} />
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => toggleStatus(c.id)} className="flex-1 px-2 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50">{c.isActive ? 'Desactivar' : 'Activar'}</button>
                  <button onClick={() => setConfirmDeleteId(c.id)} className="flex-1 px-2 py-1.5 text-xs rounded border border-red-200 text-red-700 hover:bg-red-50">Eliminar</button>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Nombre</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Tipo</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Empresa</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">CUIT/DNI</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Telefono</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Estado</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Remitos</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Fecha alta</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setSelectedClient(c)}
                  className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${!c.isActive ? 'border-l-2 border-l-red-400' : ''}`}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => goToClientPage(c)} className="font-medium text-primary-700 hover:underline">{c.name}</button>
                  </td>
                  <td className="px-4 py-3"><ClientTypeBadge clientType={c.clientType} /></td>
                  <td className="px-4 py-3">{c.company || '-'}</td>
                  <td className="px-4 py-3">{c.cuit || c.taxId || '-'}</td>
                  <td className="px-4 py-3">{c.email}</td>
                  <td className="px-4 py-3">{c.phone || '-'}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.isActive ? 'activo' : 'inactivo'} /></td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => goToClientReceipts(c.id)}
                      title="Ver remitos de este cliente"
                      className="inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      <Receipt className="h-3.5 w-3.5" />
                      {receiptCounts[String(c.id)] || 0}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDateTime(c.createdAt)}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {confirmDeleteId === c.id ? (
                      <DeleteConfirm onConfirm={() => deleteClient(c)} onCancel={() => setConfirmDeleteId(null)} />
                    ) : (
                      <div className="flex items-center gap-2">
                        <button onClick={() => goToClientPage(c)} className="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded" title="Página del cliente">
                          <ExternalLink className="h-4 w-4" />
                        </button>
                        <button onClick={() => navigate(`/admin/cuentas-corrientes/client/${c.id}`)} className="p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded" title="Ver cuenta corriente">
                          <Wallet className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleEdit(c)} className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded" title="Editar">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button onClick={() => openPasswordModal(c)} className="p-1.5 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded" title="Cambiar contraseña">
                          <KeyRound className="h-4 w-4" />
                        </button>
                        <button onClick={() => toggleStatus(c.id)} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50">
                          {c.isActive ? 'Desactivar' : 'Activar'}
                        </button>
                        <button onClick={() => setConfirmDeleteId(c.id)} className="px-2 py-1 text-xs rounded border border-red-200 text-red-700 hover:bg-red-50">
                          Eliminar
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {selectedClient && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
          <div className="w-full max-w-3xl rounded-t-xl sm:rounded-xl bg-white shadow-xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
            <div className="border-b border-gray-100 px-4 sm:px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Detalle de cliente</h2>
              <button onClick={() => setSelectedClient(null)} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Nombre</p>
                  <p className="font-medium text-gray-900">{selectedClient.name}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tipo de cliente</p>
                  <div className="mt-1"><ClientTypeBadge clientType={selectedClient.clientType} /></div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</p>
                  <a href={`mailto:${selectedClient.email}`} className="font-medium text-primary-700 hover:underline">{selectedClient.email}</a>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Teléfono</p>
                  <p className="font-medium text-gray-900">{selectedClient.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Empresa</p>
                  <p className="font-medium text-gray-900">{selectedClient.company || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">CUIT/DNI</p>
                  <p className="font-medium text-gray-900">{selectedClient.cuit || selectedClient.taxId || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Dirección</p>
                  <p className="font-medium text-gray-900">{selectedClient.address || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Estado</p>
                  <div className="mt-1"><StatusBadge status={selectedClient.isActive ? 'activo' : 'inactivo'} /></div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Remitos</p>
                  <p className="font-medium text-gray-900">{receiptCounts[String(selectedClient.id)] || 0}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Fecha de alta</p>
                  <p className="font-medium text-gray-900">{formatDateTime(selectedClient.createdAt)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Último login</p>
                  <p className="font-medium text-gray-900">{formatDateTime(selectedClient.lastLogin)}</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-4 sm:px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                {confirmDeleteId === selectedClient.id ? (
                  <DeleteConfirm onConfirm={() => deleteClient(selectedClient)} onCancel={() => setConfirmDeleteId(null)} />
                ) : (
                  <button onClick={() => setConfirmDeleteId(selectedClient.id)} className="px-3 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50">
                    Eliminar
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
                <button onClick={() => setSelectedClient(null)} className="hidden sm:block px-4 py-2 border rounded-lg hover:bg-gray-50">Cerrar</button>
                <button onClick={() => goToClientPage(selectedClient)} className="col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50"><ExternalLink className="h-4 w-4" />Página del cliente</button>
                <button onClick={() => goToClientReceipts(selectedClient.id)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Ver remitos</button>
                <button onClick={() => openPasswordModal(selectedClient)} className="px-4 py-2 border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50">Cambiar clave</button>
                <button onClick={() => handleEdit(selectedClient)} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Editar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
          <div className="w-full max-w-2xl rounded-t-xl sm:rounded-xl bg-white shadow-xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
            <div className="border-b border-gray-100 px-4 sm:px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Nuevo cliente</h2>
              <button onClick={closeCreateModal} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
            </div>
            <div className="grid grid-cols-1 gap-4 px-4 sm:px-6 py-5 overflow-y-auto">
              {createError && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{createError}</div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Nombre *</label>
                <input value={createForm.name} onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Email *</label>
                <input type="email" value={createForm.email} onChange={(e) => setCreateForm((prev) => ({ ...prev, email: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Teléfono</label>
                  <input value={createForm.phone} onChange={(e) => setCreateForm((prev) => ({ ...prev, phone: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Empresa</label>
                  <input value={createForm.company} onChange={(e) => setCreateForm((prev) => ({ ...prev, company: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">CUIT *</label>
                  <input value={createForm.cuit} onChange={(e) => setCreateForm((prev) => ({ ...prev, cuit: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Dirección</label>
                  <input value={createForm.address} onChange={(e) => setCreateForm((prev) => ({ ...prev, address: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Tipo de cliente</label>
                <select value={createForm.clientType} onChange={(e) => setCreateForm((prev) => ({ ...prev, clientType: e.target.value as 'empresa' | 'negocio' | 'particular' }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300">
                  <option value="particular">Particular (ve precios, sin remitos)</option>
                  <option value="negocio">Negocio (ve precios, con remitos)</option>
                  <option value="empresa">Empresa (precio especial/IVA, sin precios en catálogo, con remitos)</option>
                </select>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Contraseña (opcional)</label>
                  <input type="password" value={createForm.password} onChange={(e) => setCreateForm((prev) => ({ ...prev, password: e.target.value }))} placeholder="Se genera una automática si se deja vacío" className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Estado</label>
                  <select value={createForm.isActive ? 'active' : 'inactive'} onChange={(e) => setCreateForm((prev) => ({ ...prev, isActive: e.target.value === 'active' }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300">
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end border-t border-gray-100 px-4 sm:px-6 py-4">
              <div className="flex w-full sm:w-auto gap-3 [&>button]:flex-1 sm:[&>button]:flex-none">
                <button onClick={closeCreateModal} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancelar</button>
                <button onClick={saveCreateClient} disabled={isSavingCreate} className="rounded-lg bg-primary-600 px-4 py-2 text-sm text-white disabled:opacity-50">
                  {isSavingCreate ? 'Guardando...' : 'Crear cliente'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingClient && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
          <div className="w-full max-w-2xl rounded-t-xl sm:rounded-xl bg-white shadow-xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
            <div className="border-b border-gray-100 px-4 sm:px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Editar cliente</h2>
              <button onClick={closeEditModal} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
            </div>
            <div className="grid grid-cols-1 gap-4 px-4 sm:px-6 py-5 overflow-y-auto">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Nombre</label>
                <input value={editForm.name} onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Teléfono</label>
                  <input value={editForm.phone} onChange={(e) => setEditForm((prev) => ({ ...prev, phone: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Empresa</label>
                  <input value={editForm.company} onChange={(e) => setEditForm((prev) => ({ ...prev, company: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">CUIT *</label>
                  <input value={editForm.cuit} onChange={(e) => setEditForm((prev) => ({ ...prev, cuit: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Dirección</label>
                  <input value={editForm.address} onChange={(e) => setEditForm((prev) => ({ ...prev, address: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Tipo de cliente</label>
                <select value={editForm.clientType} onChange={(e) => setEditForm((prev) => ({ ...prev, clientType: e.target.value as 'empresa' | 'negocio' | 'particular' }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300">
                  <option value="particular">Particular (ve precios, sin remitos)</option>
                  <option value="negocio">Negocio (ve precios, con remitos)</option>
                  <option value="empresa">Empresa (precio especial/IVA, sin precios en catálogo, con remitos)</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Estado</label>
                <select value={editForm.isActive ? 'active' : 'inactive'} onChange={(e) => setEditForm((prev) => ({ ...prev, isActive: e.target.value === 'active' }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300">
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end border-t border-gray-100 px-4 sm:px-6 py-4">
              <div className="flex w-full sm:w-auto gap-3 [&>button]:flex-1 sm:[&>button]:flex-none">
                <button onClick={closeEditModal} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cerrar</button>
                <button onClick={saveEditClient} disabled={isSavingEdit} className="rounded-lg bg-primary-600 px-4 py-2 text-sm text-white disabled:opacity-50">
                  {isSavingEdit ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {passwordUser && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
          <div className="w-full max-w-lg rounded-t-xl sm:rounded-xl bg-white shadow-xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
            <div className="border-b border-gray-100 px-4 sm:px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Cambiar contraseña</h2>
              <button onClick={closePasswordModal} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
            </div>
            <div className="px-4 sm:px-6 py-5 space-y-4 overflow-y-auto">
              <div className="text-sm text-gray-600">
                Cliente: <span className="font-medium text-gray-900">{passwordUser.email}</span>
              </div>
              {passwordError && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{passwordError}</div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Nueva contraseña</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Confirmar contraseña</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
            </div>
            <div className="flex items-center justify-end border-t border-gray-100 px-4 sm:px-6 py-4 gap-3 [&>button]:flex-1 sm:[&>button]:flex-none">
              <button onClick={closePasswordModal} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancelar</button>
              <button onClick={savePassword} disabled={isSavingPassword} className="rounded-lg bg-primary-600 px-4 py-2 text-sm text-white disabled:opacity-50">
                {isSavingPassword ? 'Guardando...' : 'Guardar contraseña'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clientes;
